import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  getPackagePriceDisplayState,
  type PurchasePackageViewModel,
} from '../PackagesSection.helpers';
import * as S from './PackagePurchaseCard.styles';
import { CheckIcon, DiscountPercentIcon } from '@/components/UI/Icons/Icons';
import { formatNumber } from '@/utils/helpers';

type PackagePurchaseCardProps = {
  item: PurchasePackageViewModel;
  onConnect: (item: PurchasePackageViewModel) => void;
};

export function PackagePurchaseCard({
  item,
  onConnect,
}: PackagePurchaseCardProps) {
  const { t } = useTranslation();
  const { hasCombinedLabels, hasOriginalPrice, hasPromoPriceLabel } =
    getPackagePriceDisplayState(item);

  return (
    <S.Card $packageName={item.name}>
      <S.CardHeader>
        <S.NameBox>
          <S.Name>{item.name}</S.Name>
          {hasCombinedLabels && (
            <S.CompactBenefitBadge data-testid="compact-benefit-badge">
              <DiscountPercentIcon aria-hidden="true" />
            </S.CompactBenefitBadge>
          )}
        </S.NameBox>
        {hasPromoPriceLabel && !item.showPromotionLabel && (
          <S.BenefitBadge>
            {t('pages.cabinet.benefit_percent', {
              discount: item.benefitPercent,
            })}
          </S.BenefitBadge>
        )}
        {item.showPromotionLabel && (
          <S.AdditionalDiscountBadge>
            {t('pages.cabinet.additional_discount')}
          </S.AdditionalDiscountBadge>
        )}
      </S.CardHeader>
      <S.PriceBox>
        {hasOriginalPrice && (
          <S.OriginalPrice>
            {formatNumber(item.originalPrice, 0)} ₼
          </S.OriginalPrice>
        )}
        <S.PriceRow>
          <S.Price>{formatNumber(item.price, 0)} ₼</S.Price>
          <S.Period>
            {t('pages.cabinet.for_package_period', { days: item.periodDays })}
          </S.Period>
        </S.PriceRow>
      </S.PriceBox>
      <S.Features>
        {item.walletAmount !== null && (
          <S.Feature>
            <CheckIcon />
            {t('pages.cabinet.package_wallet_amount', {
              amount: formatNumber(item.walletAmount, 0),
            })}
          </S.Feature>
        )}
        {item.paidAdsCount !== null && (
          <S.Feature>
            <CheckIcon />
            {t('pages.cabinet.package_paid_ads_count', {
              adsCount: formatNumber(item.paidAdsCount, 0),
            })}
          </S.Feature>
        )}
        {Boolean(item.discount) && (
          <S.Feature>
            <CheckIcon />
            {t('pages.cabinet.discount_for_services', {
              discount: item.discount,
            })}
          </S.Feature>
        )}
      </S.Features>
      <S.ConnectButton
        appearance={item.isHighlighted ? 'secondary' : 'secondaryWhite'}
        isFullWidth
        borderRadius={14}
        onClick={() => onConnect(item)}
      >
        {t('pages.cabinet.plug_in_package', { name: item.name })}
      </S.ConnectButton>
    </S.Card>
  );
}
